import { useState } from "react";
import { Chip, STATE_META, PriorityChip } from "./entryShared";
import { ArrowRight, Warning, LinkSimple, Question } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const ExplainList = ({ label, items, tone = "text-slate-700" }) => (
  <div>
    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
    {items?.length ? (
      <ul className="mt-1 space-y-1">
        {items.map((x, i) => <li key={i} className={`text-xs ${tone} flex gap-1.5`}><span className="text-slate-300">—</span>{x}</li>)}
      </ul>
    ) : <p className="text-xs text-slate-400 mt-1">None recorded</p>}
  </div>
);

export const CaseOverview = ({ ov }) => {
  const dims = ov.readiness?.dimensions || [];
  const counts = ov.translation_counts || {};
  const [explain, setExplain] = useState(null);
  return (
    <div data-testid="case-overview" className="mt-4 space-y-6">
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-slate-900 text-white p-6" data-testid="overview-readiness-card">
          <p className="text-[10px] uppercase tracking-widest text-white/50">Overall Market Access Readiness — {ov.target_market?.name}</p>
          <p className="font-heading text-5xl font-bold mt-2">{ov.readiness?.overall}<span className="text-lg text-white/40">/100</span></p>
          <p className="text-xs text-emerald-400 mt-1 uppercase font-bold">Confidence: {ov.readiness?.confidence}</p>
          <p className="text-[11px] text-white/60 mt-3 leading-relaxed">{ov.readiness?.confidence_note}</p>
        </div>
        <div className="border border-slate-200 bg-white p-6 md:col-span-2">
          <p className="text-[10px] uppercase tracking-widest text-slate-400">Evidence translation — {ov.asset_total} assets (shared at product level, assessed per case)</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
            {[["reuse_directly", "Reuse directly", "text-emerald-700"], ["reuse_with_contextualization", "Contextualize", "text-blue-700"],
              ["localize", "Localize", "text-amber-600"], ["local_validation_required", "Validate locally", "text-orange-600"],
              ["generate_new_evidence", "Generate new", "text-red-600"], ["not_relevant", "Not relevant", "text-slate-400"]].map(([k, l, c]) => (
              <div key={k} data-testid={`count-${k}`}>
                <p className={`font-heading text-2xl font-bold ${c}`}>{counts[k] ?? 0}</p>
                <p className="text-[10px] uppercase tracking-wide text-slate-500">{l}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Estimated sequential workplan: <span className="font-bold text-slate-700">{ov.timeline_months ?? "—"} months</span> (upper bound)</p>
        </div>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Readiness dimensions — click any score to see why</p>
        <div className="grid md:grid-cols-2 gap-2">
          {dims.map((d) => (
            <button key={d.id} onClick={() => setExplain(d)} data-testid={`dim-${d.id}`}
              className="border border-slate-200 bg-white px-4 py-3 text-left hover:border-slate-900 transition-colors group">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-bold text-slate-800 flex items-center gap-1.5">{d.label}
                  <Question size={11} className="text-slate-300 group-hover:text-slate-600" /></p>
                <div className="flex items-center gap-2">
                  <Chip meta={STATE_META[d.state]} />
                  <span className="font-heading font-bold text-slate-900 text-sm w-8 text-right">{d.score}</span>
                </div>
              </div>
              <div className="h-1.5 bg-slate-100 mt-2"><div className="h-full bg-slate-900" style={{ width: `${d.score}%` }} /></div>
              <p className="text-[11px] text-slate-500 mt-2">{d.basis}</p>
              <p className="text-[11px] text-amber-700 mt-0.5">Gap: {d.gap}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="border border-red-200 bg-red-50/50 p-5" data-testid="overview-top-barriers">
          <p className="text-[10px] uppercase tracking-widest text-red-700 font-bold flex items-center gap-1"><Warning size={12} /> Top barriers</p>
          <div className="mt-2 space-y-2">
            {(ov.top_barriers || []).map((b, i) => (
              <div key={i} className="text-xs">
                <span className="font-bold text-slate-800">{b.label}:</span> <span className="text-slate-600">{b.detail}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-emerald-200 bg-emerald-50/40 p-5" data-testid="overview-top-reusable">
          <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Top reusable evidence</p>
          <div className="mt-2 space-y-2">
            {(ov.top_reusable || []).map((r, i) => (
              <div key={i} className="text-xs">
                <p className="font-bold text-slate-800">{r.name} <span className="font-normal text-slate-400">({r.transfer_score}/100)</span></p>
                <p className="text-[10px] text-slate-500">{r.classification.replaceAll("_", " ")}</p>
              </div>
            ))}
            {!ov.top_reusable?.length && <p className="text-xs text-slate-400">No reusable assets identified yet</p>}
          </div>
        </div>
        <div className="border border-amber-200 bg-amber-50/40 p-5" data-testid="overview-top-gaps">
          <p className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">Top evidence gaps</p>
          <div className="mt-2 space-y-2">
            {(ov.top_gaps || []).map((g, i) => (
              <div key={i} className="text-xs">
                <span className="font-bold text-slate-800">{g.label}:</span> <span className="text-slate-600">{g.gap}</span>
                <p className="text-[11px] text-emerald-700">→ {g.action}</p>
              </div>
            ))}
            {!ov.top_gaps?.length && <p className="text-xs text-slate-400">Gap matrix not yet developed for this case</p>}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="border border-slate-200 bg-white p-5" data-testid="overview-next-actions">
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Next best actions</p>
          <div className="mt-2 space-y-2">
            {(ov.next_best_actions || []).map((a, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <ArrowRight size={12} className="text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-slate-800">{a.activity}</p>
                  <p className="text-[10px] text-slate-400">{a.phase_name} · {a.owner} · {a.duration}{a.status === "in_progress" ? " · IN PROGRESS" : ""}</p>
                </div>
              </div>
            ))}
            {!ov.next_best_actions?.length && <p className="text-xs text-slate-400">Develop the case workplan to generate next actions</p>}
          </div>
        </div>
        <div className="border border-slate-200 bg-white p-5" data-testid="overview-dependencies">
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Critical dependencies</p>
          <div className="mt-2 space-y-2">
            {(ov.critical_dependencies || []).map((d, i) => (
              <div key={i} className="text-xs">
                <p className="font-bold text-slate-800">{d.activity}</p>
                <p className="text-[10px] text-slate-500">depends on: {d.depends_on}</p>
              </div>
            ))}
            {!ov.critical_dependencies?.length && <p className="text-xs text-slate-400">None recorded yet</p>}
          </div>
        </div>
        <div className="space-y-4">
          <div className="border border-slate-200 bg-white p-5" data-testid="overview-uncertainties">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Key uncertainties</p>
            <div className="mt-2 space-y-1.5">
              {(ov.key_uncertainties || []).map((u, i) => (
                <p key={i} className="text-[11px] text-slate-600"><span className="font-bold text-slate-800">{u.label}:</span> {u.detail}</p>
              ))}
            </div>
          </div>
          <div className="border border-slate-200 bg-white p-5" data-testid="overview-risks">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Key risks</p>
            <div className="mt-2 space-y-1.5">
              {(ov.key_risks || []).map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-[11px] text-slate-600">
                  <PriorityChip value={r.severity} /><span>{r.risk}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="border border-slate-200 bg-white p-5" data-testid="overview-module-links">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1"><LinkSimple size={12} /> Connected modules — the Access Case orchestrates, data lives once</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {(ov.module_links || []).map((m) => (
            <Link key={m.id} to={m.route} data-testid={`module-link-${m.id}`}
              className="px-3 py-1.5 text-[11px] font-bold border border-slate-200 text-slate-700 hover:bg-slate-900 hover:text-white transition-colors">
              {m.label}
            </Link>
          ))}
        </div>
      </div>

      <Sheet open={!!explain} onOpenChange={(o) => !o && setExplain(null)}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-md" data-testid="score-explain-sheet">
          {explain && (
            <>
              <SheetHeader>
                <SheetTitle className="text-left font-heading text-base">
                  {explain.label} — {explain.score}/100
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Chip meta={STATE_META[explain.state]} />
                  <span className="text-[10px] uppercase font-bold text-slate-500">Confidence: {explain.explain?.confidence}</span>
                </div>
                <p className="text-xs text-slate-600">{explain.basis}</p>
                <ExplainList label="Evidence supporting this score" items={explain.explain?.evidence_supporting} tone="text-emerald-800" />
                <ExplainList label="Evidence missing" items={explain.explain?.evidence_missing} tone="text-red-700" />
                <ExplainList label="Assumptions" items={explain.explain?.assumptions} />
                <ExplainList label="Actions that would improve this score" items={explain.explain?.improve_actions} tone="text-blue-800" />
                <p className="text-[11px] text-slate-400 italic border-l-2 border-slate-200 pl-3">No arbitrary scoring: this value is computed from the counts above and carries its uncertainty openly.</p>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
